import { useState, useCallback, useRef, useEffect } from 'react';
import { EmailApiResponse } from '@dreamai/types';
import {
  useActiveFormState,
  useFormCapture,
} from '../../../../../contexts/FormCaptureContext';

export const useEmailFormManagement = (
  email: EmailApiResponse | null,
  emailId: string,
  threadId?: string,
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>,
) => {
  const { clearActiveForm, registerActiveForm } = useFormCapture();
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editBody, setEditBody] = useState<string>('');
  const [editSubject, setEditSubject] = useState<string>('');
  const [editTo, setEditTo] = useState<string>('');
  const [editCc, setEditCc] = useState<string>('');
  const [editBcc, setEditBcc] = useState<string>('');
  const [hasAiEdits, setHasAiEdits] = useState<boolean>(false);

  // Use ref to store current values to avoid stale closures
  const currentValuesRef = useRef<Record<string, any>>({
    body: '',
    subject: '',
    to: '',
    cc: '',
    bcc: '',
  });

  // Update ref whenever any edit field changes
  useEffect(() => {
    currentValuesRef.current = {
      body: editBody,
      subject: editSubject,
      to: editTo,
      cc: editCc,
      bcc: editBcc,
    };
  }, [editBody, editSubject, editTo, editCc, editBcc]);

  // Form value getters and setters for FormCaptureContext
  const getValues = useCallback(() => {
    console.log('[getValues] from ref:', currentValuesRef.current);
    return currentValuesRef.current;
  }, []);

  // Store the restore callback from FormCaptureContext
  const restoreValuesRef = useRef<(() => Promise<void>) | null>(null);

  const setValues = useCallback(
    (
      values: Record<string, any>,
      restoreValues?: () => Promise<void>,
      isRestore?: boolean,
    ) => {
      console.log('[setValues]', values, 'isRestore:', isRestore);

      // Store the restore callback for later use
      if (restoreValues) {
        restoreValuesRef.current = restoreValues;
      }

      console.log('[Running] Yes');
      // Only set hasAiEdits to true if this is NOT a restore operation
      if (!isRestore) {
        console.log('[Running] No');
        setHasAiEdits(true);
      }

      if (values.body !== undefined) {
        setEditBody(values.body);
      }
      if (values.subject !== undefined) {
        setEditSubject(values.subject);
      }
      if (values.to !== undefined) {
        setEditTo(values.to);
      }
      if (values.cc !== undefined) {
        setEditCc(values.cc);
      }
      if (values.bcc !== undefined) {
        setEditBcc(values.bcc);
      }
    },
    [],
  );

  // Enable edit mode function that handles both local state and active form
  const enableEditMode = useCallback(() => {
    if (email) {
      // Set local edit state for all fields
      setEditBody(email.htmlBody || email.body || '');
      setEditSubject(email.subject || '');
      setEditTo(Array.isArray(email.to) ? email.to.join(', ') : email.to || '');
      setEditCc(Array.isArray(email.cc) ? email.cc.join(', ') : email.cc || '');
      setEditBcc(
        Array.isArray(email.bcc) ? email.bcc.join(', ') : email.bcc || '',
      );
      setIsEditMode(true);

      // Only register as active form if threadId is provided
      if (threadId) {
        registerActiveForm(
          threadId,
          emailId,
          emailId,
          'email',
          getValues,
          setValues,
        );
      }
    }
  }, [email, emailId, threadId, registerActiveForm, getValues, setValues]);

  // Disable edit mode function
  const disableEditMode = useCallback(() => {
    setIsEditMode(false);
    setEditBody('');
    setEditSubject('');
    setEditTo('');
    setEditCc('');
    setEditBcc('');
    setHasAiEdits(false);
    if (threadId) {
      clearActiveForm(threadId);
    }
  }, [threadId, clearActiveForm]);

  // Use reactive hook to monitor active form changes
  const currentActiveForm = useActiveFormState(threadId || '');

  // Monitor active form and exit edit mode if this component is no longer active
  useEffect(() => {
    if (!currentActiveForm || currentActiveForm.componentId !== emailId) {
      setIsEditMode(false);
      setEditBody('');
      setEditSubject('');
      setEditTo('');
      setEditCc('');
      setEditBcc('');
      setHasAiEdits(false);
    }
  }, [currentActiveForm, emailId]);

  // Update visual description based on edit mode
  useEffect(() => {
    if (updateThreadViewVisualDescription) {
      const description = isEditMode
        ? 'Editing email with form'
        : 'Viewing email';
      updateThreadViewVisualDescription(description);
    }
  }, [isEditMode, updateThreadViewVisualDescription]);

  // Toggle edit mode
  const toggleEditMode = useCallback(() => {
    if (isEditMode) {
      disableEditMode();
    } else {
      enableEditMode();
    }
  }, [isEditMode, enableEditMode, disableEditMode]);

  // AI edit management functions
  const acceptAiEdits = useCallback(() => {
    setHasAiEdits(false);
  }, []);

  const restoreAiEdits = useCallback(async () => {
    console.log('[restoreAiEdits] Attempting to restore AI edits');

    if (restoreValuesRef.current) {
      try {
        await restoreValuesRef.current();
        console.log('[restoreAiEdits] Successfully restored form values');
        // Clear the AI edits flag after successful restore
        setHasAiEdits(false);
      } catch (error) {
        console.error('[restoreAiEdits] Error restoring form values:', error);
      }
    } else {
      console.warn('[restoreAiEdits] No restore callback available');
    }
  }, []);

  return {
    isEditMode,
    editBody,
    setEditBody,
    editSubject,
    setEditSubject,
    editTo,
    setEditTo,
    editCc,
    setEditCc,
    editBcc,
    setEditBcc,
    toggleEditMode,
    enableEditMode,
    disableEditMode,
    hasAiEdits,
    acceptAiEdits,
    restoreAiEdits,
  };
};
